/**
 * Compiles the quadratic-core wasm exactly once on the main thread. The
 * resulting `WebAssembly.Module` is shared with the web workers via
 * `postMessage` so every thread instantiates the same compiled code instead of
 * each fetching and compiling its own (~26 MB raw) copy. Engines share the
 * compiled machine code across instantiations of a single `Module`, so this
 * removes the duplicated wasm code memory on the light threads.
 *
 * `WebAssembly.Module` is structured-clone serializable (not transferable), so
 * it is passed in the message body and must not be placed in a transfer list.
 */

import wasmUrl from '@/app/quadratic-core/quadratic_core_bg.wasm?url';
import { attemptChunkErrorReload } from '@/shared/utils/chunkErrorHandler';

/**
 * Thrown when the wasm asset can't be loaded because it's no longer served —
 * almost always a stale bundle after a deploy: the cached index.html/JS
 * references an old hashed asset (e.g. `quadratic_core_bg-<hash>.wasm`) that the
 * CDN now 403/404s, serving the SPA HTML fallback in its place. Feeding that
 * HTML into `WebAssembly.compile` throws an opaque
 * "expected magic word 00 61 73 6d, found 3c 21 44 4f" (`<!DO...`) CompileError.
 * This is a client/deploy-race condition, not an app bug.
 */
export class WasmAssetUnavailableError extends Error {
  /** True when a one-shot recovery reload was kicked off for this failure. */
  readonly reloadTriggered: boolean;

  constructor(message: string, reloadTriggered: boolean) {
    super(message);
    this.name = 'WasmAssetUnavailableError';
    this.reloadTriggered = reloadTriggered;
  }
}

export const isWasmAssetUnavailableError = (e: unknown): e is WasmAssetUnavailableError =>
  e instanceof WasmAssetUnavailableError;

let modulePromise: Promise<WebAssembly.Module> | null = null;

export const compileWasmModule = (): Promise<WebAssembly.Module> => {
  if (!modulePromise) {
    modulePromise = compile();
  }
  return modulePromise;
};

// The first 4 bytes of every WebAssembly binary: `\0asm`.
const WASM_MAGIC = [0x00, 0x61, 0x73, 0x6d];

const looksLikeWasm = (bytes: ArrayBuffer): boolean => {
  if (bytes.byteLength < WASM_MAGIC.length) return false;
  const header = new Uint8Array(bytes, 0, WASM_MAGIC.length);
  return WASM_MAGIC.every((b, i) => header[i] === b);
};

// A stale hashed wasm asset after a deploy is recoverable by reloading once to
// pick up the fresh index.html/asset hashes. Reuse the shared chunk-error
// reload so the wasm and JS-chunk cases share a single reload guard (no loops).
const handleStaleAsset = (message: string): WasmAssetUnavailableError => {
  const reloadTriggered = attemptChunkErrorReload();
  return new WasmAssetUnavailableError(message, reloadTriggered);
};

const compile = async (): Promise<WebAssembly.Module> => {
  try {
    const response = await fetch(wasmUrl);

    // A non-ok response means the hashed asset is gone (stale deploy). Recover
    // via reload instead of trying to compile the SPA HTML fallback body.
    if (!response.ok) {
      throw handleStaleAsset(`wasm asset request failed: ${response.status} ${response.statusText}`);
    }

    // `compileStreaming` requires the server to respond with `application/wasm`.
    // Fall back to a buffered compile when it is unavailable or the mime type is
    // wrong (e.g. some dev/proxy setups).
    if (typeof WebAssembly.compileStreaming === 'function') {
      try {
        return await WebAssembly.compileStreaming(response.clone());
      } catch (e) {
        // compileStreaming throws when the response mime type isn't
        // `application/wasm`, but also for network errors, CSP violations, or a
        // corrupt module. Log so those non-mime failures are debuggable, then
        // fall through to the buffered compile.
        console.warn('[compileWasmModule] compileStreaming failed, falling back to buffered compile:', e);
      }
    }
    const bytes = await response.arrayBuffer();

    // Guard against a 200 that's actually the HTML SPA fallback (some hosts
    // serve a missing asset as 200 index.html rather than 403/404). Same
    // stale-deploy condition — recover rather than emit an opaque CompileError.
    if (!looksLikeWasm(bytes)) {
      throw handleStaleAsset('wasm asset response is not a WebAssembly binary (likely stale deploy)');
    }

    return await WebAssembly.compile(bytes);
  } catch (e) {
    // Clear the cached promise so a later call can retry instead of forever
    // returning this rejected promise (e.g. a transient network failure).
    modulePromise = null;
    throw e;
  }
};
